import {
  getAnyDescriptionMentionRow,
  getBiologicalEntityDescriptionMentionRow,
  getEntryMentionRow,
  getFieldReferenceTaggedRows,
  getSupportedRelevantItems,
  getTaskGroupsRows,
  getWorksheetStepMentionRow,
} from 'relevant-items/shared/RelevantItemsRowBuilderUtils';
import {RelevantRowData} from 'relevant-items/shared/RelevantItemsTableUtils';
import {
  StorableRelevantItemsGraphQL,
  StorableRelevantItemsGraphQL_RelevantItemsNode_tagged as TaggedItem,
  StorableRelevantItemsGraphQL_RelevantItemsNode_filesMentioning as FilesMentioningItem,
} from 'relevant-items/storables/__generated/StorableRelevantItemsGraphQL';
import {StorableRelevantItemsFilterOption} from 'relevant-items/storables/StorableRelevantItemsFilteredTableState';
import APIUtil from 'util/APIUtil';

type TaggedStorableItem = StorableRelevantItemsGraphQL['taggedStorables'][number];

/**
 * Gets storable type label (e.g. "Container", "Box") from the storable's API id
 * so the reference copy reads e.g. "Box referenced in field".
 */
export function getStorableLabel(storableId: string): string {
  if (APIUtil.isBox(storableId)) {
    return 'Box';
  }
  if (APIUtil.isPlate(storableId)) {
    return 'Plate';
  }
  if (APIUtil.isLocation(storableId)) {
    return 'Location';
  }
  return 'Container';
}

function getTaggedRowsForFilter(
  supportedTagged: Array<TaggedItem>,
  itemsToShow: StorableRelevantItemsFilterOption,
  label: string
): Array<RelevantRowData> {
  switch (itemsToShow) {
    case StorableRelevantItemsFilterOption.ALL:
      return getFieldReferenceTaggedRows(supportedTagged, label);
    case StorableRelevantItemsFilterOption.ENTITIES:
      return getFieldReferenceTaggedRows(
        supportedTagged.filter((item) => APIUtil.isBiologicalEntity(item.id)),
        label
      );
    case StorableRelevantItemsFilterOption.ENTRIES:
      return getFieldReferenceTaggedRows(
        supportedTagged.filter((item) => APIUtil.isEntry(item.id)),
        label
      );
    default:
      return [];
  }
}

function getInventoryTaggedRowsForFilter(
  taggedStorables: Array<TaggedStorableItem>,
  itemsToShow: StorableRelevantItemsFilterOption,
  label: string
): Array<RelevantRowData> {
  switch (itemsToShow) {
    case StorableRelevantItemsFilterOption.ALL:
    case StorableRelevantItemsFilterOption.INVENTORY:
      return getFieldReferenceTaggedRows(taggedStorables, label);
    default:
      return [];
  }
}

function getFilesMentioningRowForItem(
  item: FilesMentioningItem,
  itemsToShow: StorableRelevantItemsFilterOption,
  label: string
): RelevantRowData | null {
  switch (itemsToShow) {
    case StorableRelevantItemsFilterOption.ALL:
      return (
        getWorksheetStepMentionRow(item, label) ??
        getEntryMentionRow(item, label) ??
        getAnyDescriptionMentionRow(item, label)
      );
    case StorableRelevantItemsFilterOption.WORKSHEET_STEPS:
      return getWorksheetStepMentionRow(item, label);
    case StorableRelevantItemsFilterOption.ENTRIES:
      return getEntryMentionRow(item, label);
    case StorableRelevantItemsFilterOption.ENTITIES:
      return getBiologicalEntityDescriptionMentionRow(item, label);
    case StorableRelevantItemsFilterOption.PROCESSES:
    case StorableRelevantItemsFilterOption.INVENTORY:
      return null;
    default:
      throw new Error('Invalid relevant items filter option');
  }
}

/**
 * Filters the files mentioning items to their respective types and returns an array of the data for the table.
 */
function getFilesMentioningRowsForFilter(
  supportedFilesMentioning: Array<FilesMentioningItem>,
  itemsToShow: StorableRelevantItemsFilterOption,
  label: string
): Array<RelevantRowData> {
  return supportedFilesMentioning.flatMap((item) => {
    const row = getFilesMentioningRowForItem(item, itemsToShow, label);
    return row != null ? [row] : [];
  });
}

function getTaskGroupsRowsForFilter(
  processOutputs: StorableRelevantItemsGraphQL['processOutputs'],
  processTasks: StorableRelevantItemsGraphQL['processTasks'],
  itemsToShow: StorableRelevantItemsFilterOption,
  label: string
): Array<RelevantRowData> {
  if (
    itemsToShow !== StorableRelevantItemsFilterOption.ALL &&
    itemsToShow !== StorableRelevantItemsFilterOption.PROCESSES
  ) {
    return [];
  }
  return getTaskGroupsRows(processOutputs, processTasks, label);
}

/**
 * Creates table rows for a storable's relevant items.
 *
 * Takes in the active filter option and transforms the GraphQL relevant items data and returns an array of
 * the data for the tab. The label is inferred from storable's API id
 */
export function getStorableRelevantItemRowData(
  itemsToShow: StorableRelevantItemsFilterOption,
  relevantItemsGraphQL: StorableRelevantItemsGraphQL,
  storableId: string
): Array<RelevantRowData> {
  const {filesMentioning, processOutputs, processTasks, tagged, taggedStorables} = relevantItemsGraphQL;
  const {supportedTagged, supportedFilesMentioning} = getSupportedRelevantItems(tagged, filesMentioning);
  const label = getStorableLabel(storableId);

  return [
    ...getTaggedRowsForFilter(supportedTagged, itemsToShow, label),
    ...getInventoryTaggedRowsForFilter(taggedStorables, itemsToShow, label),
    ...getFilesMentioningRowsForFilter(supportedFilesMentioning, itemsToShow, label),
    ...getTaskGroupsRowsForFilter(processOutputs, processTasks, itemsToShow, label),
  ];
}
